/**
 * __FLEET_TRACKING__ (2026-10-03) data reads for the Fleet Tracking view. Read-only.
 *
 * gps_locations facts (probe 2026-10-03): ~1-3k pings per driver per day, keyed by driver_id
 * (truck_id is usually NULL on new pings - the driver's truck comes from drivers.current_truck_id
 * or the load's truck_id), `timestamp` is naive UTC, index (driver_id, timestamp) exists, and the
 * table is NOT in the supabase_realtime publication - so live updates poll (15 s) instead of
 * subscribing. Staff (owner / dispatcher) read every row under RLS (gps_locations_select_for_staff).
 *
 * Never pull all history: every ping read is driver + time-range bounded and range-paginated
 * (PostgREST caps an un-ranged select at ~1000 rows).
 */
import { supabase } from '../supabaseClient';
import { normalizePings, naiveUtc } from '../utils/fleetTrack';

const PAGE = 1000;
const PING_COLS = 'driver_id, truck_id, latitude, longitude, speed_kmh, heading, accuracy_meters, timestamp';
const FINISHED = ['complete', 'completed', 'ready_to_bill', 'billed', 'archived', 'cancelled'];
const OPERATIONAL = ['assigned', 'en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'];
const STOP_COLS = 'id, load_id, stop_type, stop_sequence, address, place_name, city, latitude, longitude, '
  + 'planned_arrival_at, planned_departure_at, planned_seq, estimated_arrival, estimated_arrival_time, '
  + 'actual_arrival, actual_departure, actual_arrival_time, actual_departure_time, completed, dwell_actual_min';

/** Run async jobs with a small concurrency cap. */
const pool = async (items, limit, fn) => {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
};

/** Drivers + trucks for the roster. */
export const fetchFleetRoster = async () => {
  const [d, t] = await Promise.all([
    supabase.from('drivers').select('id, name, user_id, current_truck_id, status, phone').order('name').limit(1000),
    supabase.from('trucks').select('id, truck_number, license_plate, make, model, is_active').order('truck_number').limit(1000),
  ]);
  if (d.error) throw d.error;
  return { drivers: d.data || [], trucks: (t.data || []) };
};

/** All of one driver's pings in [startMs, endMs), paginated. */
export const fetchDriverPings = async (driverId, startMs, endMs, { maxRows = 20000 } = {}) => {
  const rows = [];
  for (let from = 0; from < maxRows; from += PAGE) {
    const { data, error } = await supabase.from('gps_locations')
      .select(PING_COLS)
      .eq('driver_id', driverId)
      .gte('timestamp', naiveUtc(startMs))
      .lt('timestamp', naiveUtc(endMs))
      .order('timestamp', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return rows;
};

/** { driverId: track } for every driver over [startMs, endMs). Drivers with no pings are omitted. */
export const fetchFleetTracks = async (driverIds, startMs, endMs, onProgress) => {
  const tracks = {};
  const meta = {};
  let done = 0;
  await pool(driverIds, 4, async (id) => {
    try {
      const rows = await fetchDriverPings(id, startMs, endMs);
      if (rows.length) {
        tracks[id] = normalizePings(rows);
        const withTruck = rows.filter((r) => r.truck_id).pop();
        meta[id] = { truckId: withTruck ? withTruck.truck_id : null };
      }
    } catch (e) {
      console.error('Fleet track fetch error', id, e);
    }
    done++;
    if (onProgress) onProgress(done, driverIds.length);
  });
  return { tracks, meta };
};

/**
 * Pings uploaded since sinceCreatedNaive (created_at), bounded to the last `lookbackH` hours of
 * ping time so the (driver_id, timestamp) index serves it. created_at (not timestamp) catches a
 * phone that comes back online and uploads its backlog late.
 */
export const fetchNewPings = async (driverIds, sinceCreatedNaive, { lookbackH = 12 } = {}) => {
  if (!driverIds.length) return [];
  const rows = [];
  for (let from = 0; from < 10 * PAGE; from += PAGE) {
    let q = supabase.from('gps_locations')
      .select(PING_COLS + ', created_at')
      .in('driver_id', driverIds)
      .gte('timestamp', naiveUtc(Date.now() - lookbackH * 3600000));
    if (sinceCreatedNaive) q = q.gt('created_at', sinceCreatedNaive);
    const { data, error } = await q.order('created_at', { ascending: true }).range(from, from + PAGE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return rows;
};

/** Each driver's newest ping ever (for "last seen" when he has nothing today). */
export const fetchLastPings = async (driverIds) => {
  const out = {};
  await pool(driverIds, 6, async (id) => {
    const { data } = await supabase.from('gps_locations').select(PING_COLS)
      .eq('driver_id', id).order('timestamp', { ascending: false }).limit(1);
    if (data && data[0]) {
      const tr = normalizePings(data, { maxAccuracyM: null });
      if (tr.length) out[id] = { ...tr[0], truckId: data[0].truck_id || null };
    }
  });
  return out;
};

const isoOf = (ms) => new Date(ms).toISOString();

/**
 * Loads that belong to [startMs, endMs) for any driver: a stop planned or visited that day, a
 * load started / completed / scheduled that day, and (when the day is today) every active load
 * that has a driver. Returns loads with their stops, sorted.
 */
export const fetchDayLoads = async (startMs, endMs, { includeActive = false, ymd = null } = {}) => {
  const ids = new Set();
  const sIso = isoOf(startMs), eIso = isoOf(endMs), sN = naiveUtc(startMs), eN = naiveUtc(endMs);
  const q = [
    supabase.from('load_stops').select('load_id').gte('planned_arrival_at', sIso).lt('planned_arrival_at', eIso).limit(2000),
    supabase.from('load_stops').select('load_id').gte('actual_arrival_time', sIso).lt('actual_arrival_time', eIso).limit(2000),
    supabase.from('load_stops').select('load_id').gte('actual_arrival', sN).lt('actual_arrival', eN).limit(2000),
    supabase.from('loads').select('id').not('driver_id', 'is', null).gte('started_at', sN).lt('started_at', eN).limit(2000),
    supabase.from('loads').select('id').not('driver_id', 'is', null).gte('completed_at', sN).lt('completed_at', eN).limit(2000),
  ];
  if (ymd) q.push(supabase.from('loads').select('id').not('driver_id', 'is', null).eq('scheduled_date', ymd).limit(2000));
  if (includeActive) q.push(supabase.from('loads').select('id').not('driver_id', 'is', null).in('status', OPERATIONAL).limit(2000));
  const res = await Promise.all(q);
  res.forEach((r, i) => {
    if (r.error) { console.warn('day loads probe', i, r.error.message); return; }
    (r.data || []).forEach((x) => { const id = x.load_id || x.id; if (id) ids.add(id); });
  });
  const list = Array.from(ids);
  const loads = [];
  for (let i = 0; i < list.length; i += 100) {
    const { data, error } = await supabase.from('loads')
      .select(`id, load_number, status, driver_id, truck_id, commodity, started_at, completed_at, scheduled_date, load_stops(${STOP_COLS})`)
      .in('id', list.slice(i, i + 100));
    if (error) { console.error('day loads fetch', error); continue; }
    loads.push(...(data || []));
  }
  loads.forEach((l) => {
    l.load_stops = (l.load_stops || []).slice().sort((a, b) => {
      const pa = a.planned_seq != null ? a.planned_seq : 1e6, pb = b.planned_seq != null ? b.planned_seq : 1e6;
      if (pa !== pb) return pa - pb;
      if (a.stop_type !== b.stop_type) return a.stop_type === 'pickup' ? -1 : 1;
      return (a.stop_sequence || 0) - (b.stop_sequence || 0);
    });
    l.finished = FINISHED.includes(String(l.status || ''));
  });
  return loads.sort((a, b) => String(a.load_number || '').localeCompare(String(b.load_number || '')));
};

/** Geofence events in the window, newest last. */
export const fetchDayGeofenceEvents = async (driverIds, startMs, endMs) => {
  if (!driverIds.length) return [];
  const { data, error } = await supabase.from('geofence_events')
    .select('id, driver_id, load_id, load_stop_id, event_type, stop_type, address, distance_meters, dwell_seconds, latitude, longitude, created_at')
    .in('driver_id', driverIds)
    .gte('created_at', isoOf(startMs)).lt('created_at', isoOf(endMs))
    .order('created_at', { ascending: true })
    .limit(3000);
  if (error) { console.error('geofence fetch', error); return []; }
  return data || [];
};

/** Shift sessions overlapping the window, for drivers' user_ids. [] if unreadable. */
export const fetchDayShifts = async (userIds, startMs, endMs) => {
  if (!userIds.length) return [];
  const { data, error } = await supabase.from('shift_sessions')
    .select('user_id, signed_in_at, signed_out_at')
    .in('user_id', userIds)
    .lt('signed_in_at', isoOf(endMs))
    .or(`signed_out_at.is.null,signed_out_at.gte."${isoOf(startMs)}"`)
    .order('signed_in_at', { ascending: true })
    .limit(2000);
  if (error) { console.warn('shift fetch', error.message); return []; }
  return data || [];
};

const fleetTrackingService = { fetchFleetRoster, fetchDriverPings, fetchFleetTracks, fetchNewPings, fetchLastPings, fetchDayLoads, fetchDayGeofenceEvents, fetchDayShifts };
export default fleetTrackingService;
